import { FC, useMemo } from 'react';
import styled from 'styled-components';
import {
  ConversationMessageType,
  HeroConversationMessage,
  ProductMessageData,
  CalendarInviteMessageData,
} from '@vs-online-only-app/state';
import CalendarInviteMessage from './CalendarInviteMessage';
import MediaMessage from './MediaMessage';
import ProductMessage from './ProductMessage';
import TextMessage from './TextMessage';

const AttachmentsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing(1)};
  margin-top: ${({ theme }) => theme.spacing(1)};
`;

type BaseMessage = {
  message: HeroConversationMessage;
};

type FlattenedAttachment =
  | { type: ConversationMessageType.Product; data: ProductMessageData }
  | {
      type: ConversationMessageType.Photo | ConversationMessageType.Video;
      mediaUrl: string;
      contentType: string;
      filename?: string;
      cacheUri?: string;
      contentFilterExplicit?: { rating: string };
    }
  | { type: ConversationMessageType.CalendarInvite; data: CalendarInviteMessageData };

const EmailMessage: FC<BaseMessage> = ({ message }) => {
  const attachmentsList = useMemo(() => {
    const list: FlattenedAttachment[] = [];
    const attachments = message.attributes.data?.attachments;

    if (!attachments) {
      return [];
    }

    if (attachments.products) {
      attachments.products.forEach((productData) => {
        list.push({ type: ConversationMessageType.Product, data: productData });
      });
    }

    if (attachments.medias) {
      attachments.medias.forEach((media) => {
        list.push({
          type:
            media.type === 'video'
              ? ConversationMessageType.Video
              : ConversationMessageType.Photo,
          mediaUrl: media.mediaUrl,
          contentType: media.contentType,
          filename: media.filename,
          cacheUri: media.cacheUri,
          contentFilterExplicit: media.contentFilterExplicit,
        });
      });
    }

    if (attachments.invites) {
      attachments.invites.forEach((invite) => {
        list.push({
          type: ConversationMessageType.CalendarInvite,
          data: invite,
        });
      });
    }
    return list;
  }, [message.attributes.data?.attachments]);

  return (
    <div>
      <TextMessage message={message} />
      <AttachmentsWrapper>
        {attachmentsList.map((attachment, index) => {
          switch (attachment.type) {
            case ConversationMessageType.Product: {
              const productMessage = {
                ...message,
                body: '',
                sid: message.sid,
                author: message.author,
                attributes: {
                  ...message.attributes,
                  type: ConversationMessageType.Product,
                  data: attachment.data,
                },
              };
              return <ProductMessage key={index} message={productMessage} />;
            }
            case ConversationMessageType.Photo:
            case ConversationMessageType.Video: {
              const mediaMessage = {
                ...message,
                body: '',
                sid: message.sid,
                index: message.index,
                author: message.author,
                attributes: {
                  ...message.attributes,
                  type: attachment.type,
                  mediaUrl: attachment.mediaUrl,
                },
              };
              return <MediaMessage key={index} message={mediaMessage} />;
            }
            case ConversationMessageType.CalendarInvite: {
              const calendarInviteMessage = {
                ...message,
                body: '',
                author: message.author,
                attributes: {
                  ...message.attributes,
                  type: ConversationMessageType.CalendarInvite,
                  data: attachment.data,
                },
              };
              return (
                <CalendarInviteMessage
                  key={index}
                  message={calendarInviteMessage}
                />
              );
            }
            default:
              return null;
          }
        })}
      </AttachmentsWrapper>
    </div>
  );
};

export default EmailMessage;
